import { Link } from "react-router-dom";
import { header1Styles, header2Styles, paragraphStyles } from "@consts";
import classNames from "classnames";
import { GlossarySection } from "../components/SciencePage/CatalogSections";
import {
  ScientificGapGraphic,
  ThesisResultsGraphic,
} from "../components/SciencePage/ScienceGraphics";
import { GlossaryLink } from "../components/GlossaryLink";

const thesisPdf =
  "https://github.com/Vicster97/MSci_project_atmospheric_blocking/blob/master/MSci_project_code_SPAT-Voulgarakis-1_CID_01192437_final.pdf";

const sciencePapers = [
  {
    label: "Thomas et al. (2021) · expert GTD and blocking-index disagreement",
    url: "https://wcd.copernicus.org/articles/2/581/2021/",
  },
  {
    label: "Woollings et al. (2018) · blocking and stationary-ridge classification",
    url: "https://link.springer.com/article/10.1007/s40641-018-0108-z",
  },
  {
    label: "Rousi, Kornhuber, Coumou et al. (2022) · Eurasian double-jet regimes",
    url: "https://www.nature.com/articles/s41467-022-31432-y",
  },
  {
    label: "Tibaldi & Molteni (1990) · TM90",
    url: "https://doi.org/10.3402/tellusa.v42i3.11881",
  },
  {
    label: "Dole & Gordon (1983) · DG83",
    url: "https://doi.org/10.1175/1520-0493(1983)111%3C1567:POTNAA%3E2.0.CO;2",
  },
  {
    label: "Schwierz et al. (2004) · S04",
    url: "https://doi.org/10.1175/1520-0493(2004)132%3C1849:FDIB%3E2.0.CO;2",
  },
] as const;

export const Hello = () => {
  return (
    <div className="mx-auto max-w-4xl space-y-14 px-4 py-10">
      <section>
        <p className="section-label">Science</p>
        <h1 className={`${header1Styles} mt-2`}>Atmospheric blocking & whatIsUp</h1>
        <p className={paragraphStyles}>
          whatIsUp presents research and maps on large-scale atmospheric regimes that
          drive persistent extremes. The current focus is{" "}
          <GlossaryLink termId="blocking">atmospheric blocking</GlossaryLink>, a key
          driver of European heatwaves, droughts, and flooding.
        </p>
      </section>

      <section>
        <h2 className={header2Styles}>The phenomenon</h2>
        <p className={paragraphStyles}>
          <GlossaryLink termId="blocking">Atmospheric blocking</GlossaryLink> occurs
          when persistent high-pressure systems disrupt the normal west-to-east
          jet-stream flow. These patterns can remain stable for days or weeks,
          trapping weather — heat, drought, or flood risk — in place. Named forms
          include{" "}
          <GlossaryLink termId="omega-block">omega blocks</GlossaryLink>,{" "}
          <GlossaryLink termId="stationary-ridge">stationary ridges</GlossaryLink>,
          and{" "}
          <GlossaryLink termId="high-pressure-ridge">high-pressure ridges</GlossaryLink>
          ; a longer{" "}
          <GlossaryLink termId="double-jet">double-jet regime</GlossaryLink> can also
          set the background flow without defining the local ridge shape.
        </p>
      </section>

      <section id="scientific-gap" className="scroll-mt-24">
        <h2 className={header2Styles}>The scientific gap</h2>
        <p className={paragraphStyles}>
          Three standard indices (
          <GlossaryLink termId="tm90">TM90</GlossaryLink>,{" "}
          <GlossaryLink termId="dg83">DG83</GlossaryLink>,{" "}
          <GlossaryLink termId="s04">S04</GlossaryLink>) often disagree on what counts
          as blocked: across 3,680 European summer days, all three agreed on only{" "}
          <strong className="text-accent">3 days</strong>. That disagreement motivated
          the expert{" "}
          <GlossaryLink termId="gtd">GTD</GlossaryLink> used in the thesis (
          <a
            href="https://wcd.copernicus.org/articles/2/581/2021/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-info underline decoration-info/40 underline-offset-2 hover:decoration-info"
          >
            Thomas et al., 2021
          </a>
          ).
        </p>
        <ScientificGapGraphic />
      </section>

      <section>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          <h2 className={`${header2Styles} !mb-0`}>MSci thesis results</h2>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Link
              to="/explore"
              className="btn-primary w-full justify-center !px-4 !py-2.5 text-sm sm:w-auto"
            >
              Explore
            </Link>
            <a
              href={thesisPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost w-full justify-center !px-4 !py-2.5 text-sm sm:w-auto"
            >
              Read the MSci thesis PDF
            </a>
          </div>
        </div>
        <p className={classNames(paragraphStyles, "mt-4")}>
          Using that validated dataset, a{" "}
          <GlossaryLink termId="cnn">CNN</GlossaryLink> trained on{" "}
          <GlossaryLink termId="era5">ERA5</GlossaryLink>{" "}
          <GlossaryLink termId="z500-star">Z500*</GlossaryLink> for European{" "}
          <GlossaryLink termId="jja">JJA</GlossaryLink> (1979–2018) reached{" "}
          <GlossaryLink termId="f1">F1 0.723 ± 0.052</GlossaryLink> and ~78%{" "}
          <GlossaryLink termId="gtd">GTD</GlossaryLink> recall, outperforming any
          single classical method.
        </p>

        <ThesisResultsGraphic />
      </section>

      <section>
        <h2 className={header2Styles}>Why Z500?</h2>
        <p className={paragraphStyles}>
          Atmospheric blocks appear as persistent ridges in the 500 hPa
          geopotential-height field (
          <GlossaryLink termId="z500">Z500</GlossaryLink>). Geopotential height
          accounts for variations in gravity and is measured in geopotential metres
          (gpm).
        </p>
        <div className="panel mt-4 p-4 font-mono text-sm text-ink">
          <p>Φ(z) = ∫₀ᶻ g(z′) dz′</p>
          <p className="mt-2">Z = Φ / g₀</p>
        </div>
        <p className={classNames(paragraphStyles, "mt-3")}>
          Because g ≈ g₀, geopotential height is numerically close to geometric
          height. The explorer also shows{" "}
          <GlossaryLink termId="z500-star">Z500*</GlossaryLink>, the departure from
          the zonal mean, which makes blocking-related anomalies easier to see.
        </p>
      </section>

      <section>
        <h2 className={header2Styles}>What is proven — and what isn&apos;t</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-accent/35 bg-accent/10 p-5">
            <p className="text-sm font-semibold text-accent">Proven (thesis)</p>
            <p className="mt-2 text-sm text-muted">
              Superior blocking detection · multi-method{" "}
              <GlossaryLink termId="gtd">GTD</GlossaryLink> ·{" "}
              <GlossaryLink termId="onset">onset</GlossaryLink>/
              <GlossaryLink termId="decay">decay</GlossaryLink> signals
            </p>
          </div>
          <div className="rounded-xl border border-warn/35 bg-warn/10 p-5">
            <p className="text-sm font-semibold text-warn">Not yet proven</p>
            <p className="mt-2 text-sm text-muted">
              Forecast lead time · calibrated probabilities outside JJA ·
              operational deployment
            </p>
          </div>
        </div>
        <p className={classNames(paragraphStyles, "mt-4")}>
          Labels used in the explorer —{" "}
          <GlossaryLink termId="z500">Z500</GlossaryLink>,{" "}
          <GlossaryLink termId="z500-star">Z500*</GlossaryLink>,{" "}
          <GlossaryLink termId="gradcam">Grad-CAM</GlossaryLink>,{" "}
          <GlossaryLink termId="gtd">GTD</GlossaryLink>,{" "}
          <GlossaryLink termId="onset">onset</GlossaryLink> /{" "}
          <GlossaryLink termId="mature">mature</GlossaryLink> /{" "}
          <GlossaryLink termId="decay">decay</GlossaryLink>,{" "}
          <GlossaryLink termId="validated-thesis">validated thesis</GlossaryLink> vs{" "}
          <GlossaryLink termId="new-2026">new 2026</GlossaryLink> — are defined in{" "}
          <a href="#glossary" className="text-info hover:underline">
            Labels &amp; terminology
          </a>
          .
        </p>
      </section>

      <section id="selected-references" className="scroll-mt-24">
        <h2 className={header2Styles}>Selected references</h2>
        <p className={paragraphStyles}>
          Foundational papers behind the indices, the expert ground truth, and the
          circulation patterns discussed above. Event-specific evidence also appears
          under each Explore event and in the site footer.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          {sciencePapers.map((paper) => (
            <li key={paper.url}>
              <a
                href={paper.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-info hover:underline"
              >
                {paper.label}
              </a>
            </li>
          ))}
        </ul>
      </section>

      <GlossarySection />

      <section className="panel px-5 py-6 text-center">
        <p className="text-sm text-muted">
          Open the explorer to step through real European summers on the map.
        </p>
        <div className="mt-4">
          <Link to="/explore" className="btn-primary">
            Explore
          </Link>
        </div>
      </section>
    </div>
  );
};
